/**
 * AccioStatusStrip — an always-on, one-line readout of what Accio (the execution
 * agent) is doing on this tender, so FORGE and Accio stop drifting out of sync.
 * Derived from the negotiation threads Accio writes back: overall state, per-state
 * supplier counts, quote coverage, and how recently Accio last acted (with a stale
 * badge when it's gone quiet). Polls the read-only /accio-status endpoint.
 *
 * Also surfaces the PROACTIVE engine (P0): spec coverage (quoted/awarded), a stalled
 * count, and a per-tender autopilot Pause/Resume toggle — so the operator can see the
 * bid moving and halt the auto-chasing on demand. Reads /momentum, writes /pause.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Radio, RefreshCw, Clock, AlertTriangle, Pause, Play } from 'lucide-react';
import { forgeAPI } from '../../../services/api';

const STATE_LABEL = {
  new: 'not contacted', awaiting_supplier: 'awaiting supplier', awaiting_us: 'your turn',
  quoted: 'quoted', needs_human: 'needs review', closed: 'closed',
};
const STATE_COLOR = {
  new: '#6b7785', awaiting_supplier: '#d97706', awaiting_us: '#2563eb',
  quoted: '#059669', needs_human: '#b91c1c', closed: '#6b7785',
};
const STALE_MS = 6 * 3600 * 1000;   // no Accio activity in 6h → flag as possibly stalled

const ago = (ts) => {
  if (!ts) return null;
  const s = (Date.now() - new Date(ts).getTime()) / 1000;
  if (Number.isNaN(s)) return null;
  if (s < 90) return 'just now';
  if (s < 3600) return `${Math.round(s / 60)}m ago`;
  if (s < 86400) return `${Math.round(s / 3600)}h ago`;
  return `${Math.round(s / 86400)}d ago`;
};

const AccioStatusStrip = ({ tenderId }) => {
  const [snap, setSnap] = useState(null);
  const [mom, setMom] = useState(null);
  const [busy, setBusy] = useState(false);
  const [pausing, setPausing] = useState(false);

  const load = useCallback(() => {
    if (!tenderId) return;
    setBusy(true);
    Promise.allSettled([forgeAPI.accioStatus(tenderId), forgeAPI.momentum(tenderId)])
      .then(([s, m]) => {
        if (s.status === 'fulfilled') setSnap(s.value);
        if (m.status === 'fulfilled') setMom(m.value);
      })
      .finally(() => setBusy(false));
  }, [tenderId]);

  useEffect(() => { setSnap(null); setMom(null); load(); }, [load]);
  // Poll while the pane is open so the strip tracks Accio without a manual refresh.
  useEffect(() => {
    if (!tenderId) return undefined;
    const id = setInterval(load, 20000);
    return () => clearInterval(id);
  }, [tenderId, load]);

  const togglePause = useCallback(() => {
    if (!tenderId || !mom) return;
    setPausing(true);
    forgeAPI.pauseSourcing(tenderId, !mom.paused)
      .then(() => load())
      .catch(() => {})
      .finally(() => setPausing(false));
  }, [tenderId, mom, load]);

  const sum = snap?.summary || {};
  const inNeg = sum.suppliers_in_negotiation || 0;
  const withQuote = sum.with_quote || 0;
  const byState = sum.by_state || {};
  const last = sum.last_accio_action_at;
  const lastLabel = ago(last);
  const stale = last && (Date.now() - new Date(last).getTime()) > STALE_MS;

  const idle = inNeg === 0 && withQuote === 0;
  const runLabel = idle ? 'No live negotiations yet'
    : `Sourcing · ${inNeg} in negotiation${withQuote ? ` · ${withQuote} quoted` : ''}`;

  // Proactive-engine readout (P0): spec coverage + stalled count + autopilot state.
  const totalLines = mom?.line_items_total || 0;
  const coverage = totalLines
    ? `${mom.line_items_quoted}/${totalLines} lines quoted${mom.line_items_awarded ? ` · ${mom.line_items_awarded} awarded` : ''}`
    : null;
  const stalled = mom?.stalled || 0;
  const paused = !!mom?.paused;

  return (
    <div className={`forge-ck-accio ${idle ? 'idle' : ''} ${paused ? 'paused' : ''}`}>
      <span className="forge-ck-accio-dot" aria-hidden><Radio size={13} /></span>
      <span className="forge-ck-accio-run">{runLabel}</span>
      {!idle && Object.entries(byState).map(([st, n]) => (
        <span key={st} className="forge-ck-accio-badge"
          style={{ background: `${STATE_COLOR[st] || '#6b7785'}1a`, color: STATE_COLOR[st] || '#6b7785' }}>
          {n} {STATE_LABEL[st] || st}
        </span>
      ))}
      {coverage && (
        <span className="forge-ck-accio-badge" style={{ background: '#2563eb1a', color: '#2563eb' }}
          title="Spec coverage — line items with at least one quote">
          {coverage}
        </span>
      )}
      {stalled > 0 && (
        <span className="forge-ck-accio-badge" style={{ background: '#d977061a', color: '#b45309' }}
          title={mom?.stalled_suppliers?.length ? `Silent: ${mom.stalled_suppliers.join(', ')}` : 'Suppliers gone quiet'}>
          {stalled} stalled
        </span>
      )}
      <span className="forge-ck-accio-spacer" />
      {paused && (
        <span className="forge-ck-accio-badge" style={{ background: '#6b77851a', color: '#6b7785' }}>
          autopilot paused
        </span>
      )}
      {lastLabel && (
        <span className={`forge-ck-accio-last ${stale ? 'stale' : ''}`} title={last}>
          {stale ? <AlertTriangle size={12} /> : <Clock size={12} />}
          {stale ? `Accio quiet · last ${lastLabel}` : `Accio active · ${lastLabel}`}
        </span>
      )}
      {mom && (
        <button className="forge-btn-ghost forge-ck-accio-refresh" onClick={togglePause}
          disabled={pausing} title={paused ? 'Resume proactive sourcing for this tender'
            : 'Pause proactive sourcing (stop auto follow-ups + Accio nudges)'}>
          {paused ? <Play size={13} /> : <Pause size={13} />}
        </button>
      )}
      <button className="forge-btn-ghost forge-ck-accio-refresh" onClick={load}
        disabled={busy} title="Refresh Accio status">
        <RefreshCw size={13} className={busy ? 'forge-spin' : ''} />
      </button>
    </div>
  );
};

export default AccioStatusStrip;
